import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useAppLocale } from '../../providers/LocaleProvider';
import {
  learnerImageCarouselLogicalIndex,
  learnerImageCarouselNextButtonSide,
  learnerImageCarouselPrevButtonSide,
  learnerImageCarouselSwipeAction,
  learnerImageCarouselTrackTransform,
  learnerImageCarouselTrackUrls,
} from '../../utils/learnerImageCarouselMotion';
import { formatImageCarouselCounter } from '../../utils/imageCarouselLabels';
import { LearnerExpandableImage } from './LearnerExpandableImage';

function resolveImageSrc(imageSrc: string): string {
  if (imageSrc.startsWith('data:')) return imageSrc;
  if (imageSrc.startsWith('/9j/') || imageSrc.startsWith('iVBORw0KG')) {
    return `data:image/png;base64,${imageSrc}`;
  }
  return imageSrc;
}

export const LearnerImageCarousel: React.FC<{ urls: string[]; alt: string; className?: string }> = ({
  urls,
  alt,
  className = '',
}) => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [imageAspectRatios, setImageAspectRatios] = React.useState<Array<number>>([]);
  const [containerHeight, setContainerHeight] = React.useState<number | null>(null);
  const [dragOffsetPx, setDragOffsetPx] = React.useState(0);
  const [isTouchDragging, setIsTouchDragging] = React.useState(false);
  const touchStartXRef = React.useRef<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { isDark: isDarkTheme } = useTheme();
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const overlayBtnClass =
    'absolute top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/60 text-white transition-all duration-200 opacity-60 hover:opacity-100 z-10';

  const recalcContainerHeight = React.useCallback(() => {
    const el = containerRef.current;
    if (!el || imageAspectRatios.length !== urls.length || !imageAspectRatios.every((r) => r > 0)) {
      return;
    }
    const containerWidth = el.offsetWidth;
    const heights = imageAspectRatios.map((aspectRatio) => containerWidth / aspectRatio);
    setContainerHeight(Math.max(...heights));
  }, [imageAspectRatios, urls.length]);

  React.useEffect(() => {
    recalcContainerHeight();
  }, [recalcContainerHeight]);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => recalcContainerHeight());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalcContainerHeight]);

  const handleImageLoad = (idx: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setImageAspectRatios((prev) => {
      const newRatios = [...prev];
      newRatios[idx] = aspectRatio;
      return newRatios;
    });
  };

  const goToPrevious = () => {
    if (currentImageIndex <= 0) return;
    setCurrentImageIndex((prev) => prev - 1);
  };

  const goToNext = () => {
    if (currentImageIndex >= urls.length - 1) return;
    setCurrentImageIndex((prev) => prev + 1);
  };

  const goToIndex = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    touchStartXRef.current = e.touches[0]!.clientX;
    setIsTouchDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current == null || e.touches.length !== 1) return;
    const dx = e.touches[0]!.clientX - touchStartXRef.current;
    setDragOffsetPx(dx);
  };

  const handleTouchEnd = () => {
    const action = learnerImageCarouselSwipeAction(dragOffsetPx, isRtl);
    if (action === 'next') goToNext();
    else if (action === 'prev') goToPrevious();
    touchStartXRef.current = null;
    setDragOffsetPx(0);
    setIsTouchDragging(false);
  };

  if (urls.length === 0) return null;

  const slidePercent = 100 / urls.length;
  const trackUrls = learnerImageCarouselTrackUrls(urls, isRtl);
  const PrevIcon = isRtl ? ArrowRight : ArrowLeft;
  const NextIcon = isRtl ? ArrowLeft : ArrowRight;

  return (
    <div className={`relative w-full ${className}`}>
      <div
        ref={containerRef}
        dir="ltr"
        className="relative overflow-hidden rounded-lg w-full touch-pan-y"
        style={containerHeight ? { height: `${containerHeight}px` } : { minHeight: '160px' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div
          className={`flex h-full ${isTouchDragging ? '' : 'transition-transform duration-500 ease-in-out'}`}
          style={{
            transform: learnerImageCarouselTrackTransform(
              currentImageIndex,
              urls.length,
              dragOffsetPx,
              isRtl,
            ),
            width: `${urls.length * 100}%`,
          }}
        >
          {trackUrls.map((imageSrc, trackIdx) => {
            const logicalIdx = learnerImageCarouselLogicalIndex(trackIdx, urls.length, isRtl);
            return (
            <div
              key={`${imageSrc}-${logicalIdx}`}
              className="flex h-full flex-shrink-0 items-center justify-center px-1"
              style={{ width: `${slidePercent}%` }}
            >
              <LearnerExpandableImage
                src={resolveImageSrc(imageSrc)}
                alt={`${alt} - Image ${logicalIdx + 1}`}
                fillContainer
                className="h-full w-full rounded-lg object-contain"
                onLoad={(e) => handleImageLoad(logicalIdx, e)}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            );
          })}
        </div>

        {urls.length > 1 && (
          <>
            {currentImageIndex > 0 && (
              <button
                type="button"
                onClick={goToPrevious}
                className={`${learnerImageCarouselPrevButtonSide(isRtl)} ${overlayBtnClass}`}
                aria-label="Previous image"
              >
                <PrevIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            )}
            {currentImageIndex < urls.length - 1 && (
              <button
                type="button"
                onClick={goToNext}
                className={`${learnerImageCarouselNextButtonSide(isRtl)} ${overlayBtnClass}`}
                aria-label="Next image"
              >
                <NextIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            )}
          </>
        )}
      </div>

      {urls.length > 1 && (
        <>
          <div className="flex justify-center gap-1 mt-2">
            {urls.map((_, imgIdx) => (
              <button
                key={imgIdx}
                type="button"
                onClick={() => goToIndex(imgIdx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentImageIndex === imgIdx
                    ? isDarkTheme
                      ? 'bg-blue-400 w-6'
                      : 'bg-blue-600 w-6'
                    : isDarkTheme
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                }`}
                aria-label={`Go to image ${imgIdx + 1}`}
              />
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1">
            {formatImageCarouselCounter(currentImageIndex + 1, urls.length)}
          </p>
        </>
      )}
    </div>
  );
};
